import { uiText } from '../utils/i18n';
import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../store';
import type { WSOutgoingMessage } from '../types';
import { Avatar } from './shared/Avatar';
import { getAvatar } from '../utils/avatar-store';
import { useT } from '../utils/i18n';
import { relativeTimeCN } from '../utils/format-time';
import { AgentDetailModal } from './AgentDetailModal';
import { CreateAgentModal } from './CreateAgentModal';
import { InviteModal } from './InviteModal';
import s from './ContactsPage.module.css';

interface WorkspaceMember {
  userId: string;
  email?: string;
  name?: string;
  avatarUrl?: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: number;
}

interface PendingInvite {
  id: string;
  email: string;
  role: string;
  status: string;
  createdAt: number;
  expiresAt: number;
}

interface ContactsPageProps {
  send: (msg: WSOutgoingMessage) => void;
}

const fmtTok = (n: number) => n >= 1e6 ? (n / 1e6).toFixed(2) + 'M' : (n / 1e3).toFixed(0) + 'K';

const MEMBER_COLORS = ['#3a5a8a', '#8a5a3a', '#5a8a6a', '#8a3a5a', '#5a3a8a', '#8a8a3a'];

function SectionHead({ label, count, cta, ctaColor, onCta }: {
  label: string;
  count?: number;
  cta?: string;
  ctaColor?: string;
  onCta?: () => void;
}) {
  return (
    <div className={s.sectionHead}>
      <span className={s.sectionLabel}>{label}</span>
      {count != null && (
        <span className={s.sectionCount}>· {String(count).padStart(2, '0')}</span>
      )}
      <span className={s.sectionDash} />
      {cta && (
        <button
          className={s.sectionCta}
          style={{ background: ctaColor || 'var(--tomato)' }}
          onClick={onCta}
        >
          {cta}
        </button>
      )}
    </div>
  );
}

interface ActivityEvent {
  id: string;
  type: 'task_started' | 'task_completed' | 'task_failed';
  botName: string;
  chatId: string;
  userId?: string;
  prompt?: string;
  responsePreview?: string;
  costUsd?: number;
  durationMs?: number;
  errorMessage?: string;
  timestamp: number;
}

interface LeaderEntry {
  name: string;
  costUsd: number;
  taskCount: number;
  isBot: boolean;
}

interface OrgLogEntry {
  id: number;
  workspace_id: string;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  target_name: string | null;
  detail: string | null;
  created_at: number;
}

const ORG_ACTION_MAP: Record<string, { label: string; color: string; icon: string }> = {
  member_joined: { label: '加入了组织', color: 'var(--olive)', icon: '+' },
  member_removed: { label: '被移出了组织', color: 'var(--tomato)', icon: '−' },
  agent_created: { label: '创建了 Agent', color: 'var(--sky)', icon: '+' },
  agent_deleted: { label: '删除了 Agent', color: 'var(--tomato)', icon: '×' },
  agent_updated: { label: '编辑了 Agent', color: 'var(--olive)', icon: '✎' },
};

export function ContactsPage({ send }: ContactsPageProps) {
  const bots = useStore((s) => s.bots);
  const connected = useStore((s) => s.connected);
  const setView = useStore((s) => s.setView);
  const getOrCreateBotSession = useStore((s) => s.getOrCreateBotSession);
  const token = useStore((s) => s.token);
  const [detailBot, setDetailBot] = useState<{ name: string; description?: string } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [activityEvents, setActivityEvents] = useState<ActivityEvent[]>([]);
  const [orgLog, setOrgLog] = useState<OrgLogEntry[]>([]);
  const t = useT();

  const userEmail = useStore((s) => s.userEmail);

  const fetchMembers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/auth/workspace/members', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
        const email = useStore.getState().userEmail;
        const me = (data.members || []).find((m: WorkspaceMember) => m.email === email);
        setIsOwner(me?.role === 'owner');
      }
    } catch { /* ignore */ }
    setMembersLoading(false);
  }, [token]);

  const fetchInvites = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/auth/workspace/invites', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInvites(data.invites || []);
      }
    } catch { /* ignore */ }
  }, [token]);

  const fetchActivity = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/activity/events?limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setActivityEvents(data.events || []);
      }
    } catch { /* ignore */ }
  }, [token]);

  const fetchOrgLog = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/org-log?limit=20', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrgLog(data.entries || []);
      }
    } catch { /* ignore */ }
  }, [token]);

  const activeView = useStore((s) => s.activeView);
  useEffect(() => {
    if (activeView === 'team') {
      fetchMembers();
      fetchInvites();
      fetchActivity();
      fetchOrgLog();
    }
  }, [activeView, fetchMembers, fetchInvites, fetchActivity, fetchOrgLog]);

  const handleRemoveMember = async (userId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`/auth/workspace/members?userId=${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMembers((prev) => prev.filter((m) => m.userId !== userId));
      }
    } catch { /* ignore */ }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`/auth/workspace/invite?id=${inviteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
      }
    } catch { /* ignore */ }
  };

  const handleInviteClose = () => {
    setShowInvite(false);
    fetchInvites();
  };

  const roleLabel = (role: string) => {
    if (role === 'owner') return t('team.owner');
    return t('team.member');
  };

  const handleOpenChat = useCallback((botName: string) => {
    getOrCreateBotSession(botName);
    setView('chat');
  }, [getOrCreateBotSession, setView]);

  const handleOpenDm = useCallback((member: WorkspaceMember) => {
    const displayName = member.name || member.email?.split('@')[0] || 'User';
    const dmKey = `dm:${member.userId}`;
    getOrCreateBotSession(dmKey);
    const state = useStore.getState();
    if (state.activeSessionId) {
      const session = state.sessions.get(state.activeSessionId);
      if (session && session.botName === dmKey && session.title !== displayName) {
        state.renameSession(state.activeSessionId, displayName);
      }
    }
    setView('chat');
  }, [getOrCreateBotSession, setView]);

  // Build leaderboard from activity events — aggregate cost by bot
  const leaderboard: LeaderEntry[] = (() => {
    const map = new Map<string, { costUsd: number; taskCount: number }>();
    for (const ev of activityEvents) {
      if (ev.type !== 'task_completed') continue;
      const key = ev.botName;
      const cur = map.get(key) || { costUsd: 0, taskCount: 0 };
      cur.costUsd += ev.costUsd || 0;
      cur.taskCount += 1;
      map.set(key, cur);
    }
    return Array.from(map.entries())
      .map(([name, { costUsd, taskCount }]) => ({ name, costUsd, taskCount, isBot: true }))
      .filter((e) => e.costUsd > 0)
      .sort((a, b) => b.costUsd - a.costUsd)
      .slice(0, 10);
  })();
  const maxCost = leaderboard.length > 0 ? leaderboard[0].costUsd : 1;
  const totalCost = leaderboard.reduce((s, e) => s + e.costUsd, 0);


  const totalMembers = members.length + invites.length;

  return (
    <div className={s.container}>
      {/* Header */}
      <header className={s.header}>
        <div className={s.headerLeft}>
          <span className={s.eyebrow}>your team</span>
          <h1 className={s.title}>{t('contacts.title')}</h1>
        </div>
        <div className={s.headerSpacer} />
        {connected && (
          <div className={s.statusBadge}>● {members.length} {t('team.members')} · {bots.length} {t('contacts.agents')}</div>
        )}
      </header>

      <div className={s.inner}>
        {/* Members section */}
        {!membersLoading && (totalMembers > 0) && (
          <section className={s.section}>
            <SectionHead
              label={t('team.members')}
              count={totalMembers}
              cta={isOwner ? `+ ${t('team.invite')}` : undefined}
              ctaColor="var(--olive)"
              onCta={() => setShowInvite(true)}
            />
            <div className={s.membersGrid}>
              {members.map((member, idx) => {
                const initial = (member.name || member.email || 'U').charAt(0).toUpperCase();
                const color = MEMBER_COLORS[idx % MEMBER_COLORS.length];
                return (
                  <div key={member.userId} className={s.card}>
                    <div className={s.cardCorner} />
                    <div className={s.cardTop}>
                      <div className={s.memberAvatar} style={{ background: color }}>

                        {member.avatarUrl ? (
                          <img src={member.avatarUrl} alt="" className={s.memberAvatarImg} />
                        ) : (
                          <span className={s.memberAvatarInitial}>{initial}</span>
                        )}
                      </div>
                      <div className={s.cardInfo}>
                        <div className={s.cardNameRow}>
                          <span className={s.cardName}>{member.name || member.email?.split('@')[0] || 'User'}</span>
                          <span
                            className={s.roleBadge}
                            style={{ background: member.role === 'owner' ? 'var(--butter)' : 'var(--bg-paper-dk)' }}
                          >
                            {roleLabel(member.role)}
                          </span>
                        </div>
                        {member.email && <div className={s.cardEmail}>{member.email}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {member.email !== userEmail && (
                          <button className={s.chatBtn} onClick={() => handleOpenDm(member)}>
                            {t('contacts.openChat')} →
                          </button>
                        )}
                        {isOwner && member.role !== 'owner' && member.email !== userEmail && (
                          <button className={s.removeBtn} onClick={() => handleRemoveMember(member.userId)}>
                            {t('team.remove')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Pending invites */}
              {isOwner && invites.map((inv) => (
                <div key={inv.id} className={s.ghostCard}>
                  <div className={s.cardTop}>
                    <div className={s.memberAvatar} style={{ background: '#3a3a2e' }}>
                      <span className={s.memberAvatarInitial} style={{ fontSize: 18 }}>✉</span>
                    </div>
                    <div className={s.cardInfo}>
                      <div className={s.cardNameRow}>
                        <span className={s.cardName}>{inv.email}</span>
                        <span className={s.roleBadge} style={{ background: 'var(--bg-paper-dk)', color: 'var(--text-dim)' }}>
                          PENDING
                        </span>
                      </div>
                    </div>
                    <button className={s.removeBtn} onClick={() => handleRevokeInvite(inv.id)}>
                      {t('team.revoke')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Agents section */}
        <section className={s.section}>
          <SectionHead
            label="AGENTS"
            count={bots.length}
            cta={t('contacts.newAgent')}
            ctaColor="var(--tomato)"
            onCta={() => setShowCreate(true)}
          />
          <div className={s.agentsGrid}>
            {bots.map((bot) => {
              const isOnline = connected;
              const statusLabel = isOnline ? t('contacts.statusOnline') : t('contacts.statusOffline');
              const avatar = getAvatar(bot.name);
              // Get bot stats from activity events
              const botEntry = leaderboard.find((e) => e.name === bot.name);

              return (
                <div
                  key={bot.name}
                  className={s.card}
                  onClick={() => setDetailBot({ name: bot.name, description: bot.description })}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={s.cardTop}>
                    <Avatar
                      name={bot.name}
                      size="lg"
                      status={isOnline ? 'online' : 'offline'}
                      imageUrl={avatar.imageUrl}
                      presetColor={avatar.presetColor}
                    />
                    <div className={s.cardInfo}>
                      <div className={s.cardName} style={{ fontSize: 18 }}>{bot.name}</div>
                      <div className={s.agentStatusText}>
                        <span className={s.agentModelTag}>{(bot.engine || 'AI').toUpperCase()}</span>
                        {' · '}
                        <span className={isOnline ? s.agentOnline : s.agentOffline}>{statusLabel}</span>
                      </div>
                    </div>
                    <button
                      className={s.chatBtn}
                      onClick={(e) => { e.stopPropagation(); handleOpenChat(bot.name); }}
                    >
                      {t('contacts.openChat')} →
                    </button>
                  </div>

                  {bot.description && <p className={s.description}>{bot.description}</p>}
                </div>
              );
            })}

            {bots.length === 0 && (
              <div className={s.empty}>
                {connected ? t('contacts.noAgents') : t('contacts.connecting')}
              </div>
            )}
          </div>
        </section>

        {/* Bottom row: Token Leaderboard + Activity Log */}
        <section className={s.section}>
          <SectionHead label={t('team.stats') || uiText("统计")} />
        </section>
        <div className={s.bottomGrid}>
          {/* Token Leaderboard */}
          <div className={s.panelCard}>
            <div className={s.panelCorner} />
            <div className={s.panelHeader}>
              <span className={s.panelTitle} style={{ color: 'var(--tomato)' }}>{uiText("Token 用量榜")}</span>
              <span className={s.panelSubtitle}>{uiText("本月")}</span>
              <div className={s.panelSpacer} />
              {totalCost > 0 && (
                <span className={s.panelTotal}>
                  ${totalCost.toFixed(2)}
                </span>
              )}
            </div>
            {leaderboard.length > 0 ? (
              <div className={s.leaderList}>
                {leaderboard.map((entry, i) => {
                  const avatar = getAvatar(entry.name);
                  const estTokens = Math.round((entry.costUsd || 0) / 0.000003);
                  return (
                    <div key={entry.name} className={s.leaderRow}>
                      <span className={`${s.leaderRank} ${i === 0 ? s.leaderRankFirst : ''}`}>{i + 1}</span>
                      <Avatar
                        name={entry.name}
                        size="sm"
                        imageUrl={avatar.imageUrl}
                        presetColor={avatar.presetColor}
                      />
                      <div className={s.leaderInfo}>
                        <div className={s.leaderNameRow}>
                          <span className={s.leaderName}>{entry.name}</span>
                          <span className={s.leaderTokens}>{fmtTok(estTokens)}</span>
                        </div>
                        <div className={s.leaderBar}>
                          <div
                            className={s.leaderBarFill}
                            style={{
                              width: `${(entry.costUsd / maxCost) * 100}%`,
                              background: avatar.presetColor || 'var(--olive)',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={s.empty} style={{ padding: '20px 0' }}>{uiText("暂无数据")}</div>
            )}
          </div>

          {/* Org Log */}
          <div className={s.panelCard}>
            <div className={s.panelCorner} />
            <div className={s.panelHeader}>
              <span className={s.panelTitle} style={{ color: 'var(--olive)' }}>{uiText("团队日志")}</span>
              <div className={s.panelSpacer} />
            </div>
            {orgLog.length > 0 ? (
              <div className={s.logTimeline}>
                <div className={s.logLine} />
                <div className={s.logEntries}>
                  {orgLog.map((entry) => {
                    const action = ORG_ACTION_MAP[entry.action] || { label: entry.action, color: 'var(--text-dim)', icon: '·' };
                    const actor = entry.actor_name || '系统';
                    return (
                      <div key={entry.id} className={s.logEntry}>
                        <div className={s.logDot} style={{ background: action.color }}>{action.icon}</div>
                        <div className={s.logContent}>
                          <div className={s.logText}>
                            <strong>{actor}</strong> {uiText(action.label)}
                            {entry.target_name && <> <strong>{entry.target_name}</strong></>}
                          </div>
                          <div className={s.logTime}>{relativeTimeCN(entry.created_at * 1000)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className={s.empty} style={{ padding: '20px 0' }}>{uiText("暂无日志")}</div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {detailBot && (
        <AgentDetailModal
          botName={detailBot.name}
          description={detailBot.description}
          onClose={() => setDetailBot(null)}
        />
      )}

      {showCreate && (
        <CreateAgentModal
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      )}

      {showInvite && <InviteModal onClose={handleInviteClose} />}
    </div>
  );
}
